<template>
  <div v-if="question && isActive" class="exam w-full lg:w-[800px] mx-auto">

    <question-timer v-if="exam" :seconds="exam.time_per_questions" @timeout="questionFinished"/>

    <question-timer v-if="showAnswerTimer" :seconds="1"></question-timer>

    <freetext-selfcheck
        v-if="showFreeTextCompare"
        :correct-answer="question.choices[0].text"
        :question-id="question.id"
        :question-text="question.text"
        :type="type"
        :mistake-id="question.mistake_id"
        :given-answer="answer"
        @answered="onSelfcheckAnswer"
    />

    <div v-else class="">

      <div class="bg-white border border-gray-200 shadow-sm max-h-[280px] overflow-y-auto overscroll-auto md:max-h-full font-medium text-sm lg:text-xl mb-4 lg:mb-8 rounded-lg">
        <div v-if="question.context" class="px-5 pt-2 pb-2 bg-light-blue text-blue lg:text-base rounded-t-lg">
          <div class="flex items-center font-bold ">
            <icon class="w-4 lg:w-5 h-4 lg:h-5 mr-2 p-1 bg-primary-500 fill-light-blue rounded-full" name="info"/>
            {{ __('question.context_info_title') }}
          </div>
          <div class="pt-2" v-html="question.context"></div>
        </div>
        <div class="px-5 py-4" v-html="question.text"></div>
      </div>

      <div class="mb-4">
        <single-choice-question v-if="1 === question.type" :question="question" @update="setAnswer "/>
        <multiple-choice-question v-if="2 === question.type" :question="question" @update="setAnswer"/>
        <text-question v-if="3 === question.type" :question="question" @update="setAnswer"/>
      </div>

      <div class="btn-group relative flex items-center justify-between">
        <a href="#" class="uxfeedback--open underline text-sm text-primary-500 block"><i
            class="fa-solid fa-masks-theater"></i> Feedback geben</a>

        <loading-button :loading="isLoading" :class="mistakeBtnClasses"
                        class="flex disabled:opacity-25 disabled:cursor-not-allowed inline-block mt-2 text-sm"
                        @click="addForLearning()">
          <i v-if="mistakeAdded" class="fa fa-check mr-2"></i>
          <icon v-else-if="!isLoading" class="fill-red-500 mr-2" name="archive"></icon>
          <i v-else class="fa fa-spinner fa-spin mr-2"></i>
          <span v-if="isLoading">Bitte warten...</span>
          <span v-else-if="mistakeAdded">Zur Wiederholung markiert</span>
          <span v-else>Später nochmal üben</span>
        </loading-button>

        <div v-if="showProceedButtons" class="continue-btn-group">
          <loading-button v-if="!hideContinueBtn" :disabled="!hasValidAnswer" :loading="isLoading"
                          class="btn-success disabled:opacity-25 disabled:cursor-not-allowed inline-block mt-2"
                          @click="showNextQuestion()"> {{ __('question.continue') }}
          </loading-button>

          <loading-button v-if="!hideBtn" :disabled="!hasValidAnswer" :loading="isLoading"
                          class="btn-primary disabled:opacity-25 disabled:cursor-not-allowed inline-block mt-2"
                          @click="questionFinished()"> {{ __('question.next') }}
          </loading-button>
        </div>
      </div>

    </div>

  </div>

</template>

<script>

import LoadingButton from '../../Shared/LoadingButton'
import {sendAnswer} from '../../api/QuestionApi'
import {sendMistakeAnswer, createMistake} from '../../api/MistakeApi'
import Icon from '../Icon.vue'

export default {

  components: {
    Icon,
    LoadingButton,
  },

  props: {
    exam: {
      type: Object,
      required: false,
    },
    test: {
      type: Object,
      required: false,
    },
    question: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
      default: 'exam',
    },
    options: {
      required: false,
      type: Object,
    },
    isActive: Boolean,
    isLast: Boolean,
  },
  emits: ['answer', 'testPublished', 'selfcheck'],

  data() {
    return {
      showFreeTextCompare: false,
      answer: null,
      isLoading: false,
      TIMEOUT: 2000,
      showProceedButtons: true,
      hideBtn: false,
      hideContinueBtn: true,
      showAnswerTimer: false,
      tempResult: Object,
      mistakeAdded: false,
    }
  },

  computed: {
    hasValidAnswer() {
      if (this.answer && Array.isArray(this.answer)) {
        return this.answer.length === this.question.amount_of_answers
      }
      return !!(this.answer)
    },

    showDirectAnswer() {
      if (this.type !== 'exam' && !this.exam) {
        return true
      }
      return (this.exam && this.exam.direct_solution)
    },

    showAnswerDirectly() {
      return this.question.type === 3 && this.type !== 'exam'
    },

    mistakeBtnClasses() {
      return {
        'text-success-500': this.mistakeAdded,
        'text-red-500': !this.mistakeAdded,
      }
    },
  },

  watch: {
    isActive: function () {
      // eslint-disable-next-line no-undef
      if (ux_custom) {
        // eslint-disable-next-line no-undef
        ux_custom.question_id = this.question.id
        // eslint-disable-next-line no-undef
        ux_custom.question_text = this.question.text
      }
    },
  },

  methods: {

    addForLearning() {
      this.isLoading = true
      let data = {
        question_id: this.question.id,
        context: 'marked',
      }
      createMistake(data)
          .then(() => {
            this.isLoading = false
            this.mistakeAdded = true
            //only allow skipping the question if in practice mode
            if (this.type === 'practice') {
              this.hideBtn = true
              this.hideContinueBtn = false
            }
          })
    },

    setAnswer(answer) {
      this.answer = answer
    },

    /**
     * Triggered when a question is finished,
     * will be send through api to database
     *
     * @param missed when time was up, this value will be set to true
     *
     * @since 0.1 depending on type, different route
     *
     * @returns {Promise<void>}
     */
    async questionFinished(missed = false) {

      // collect data
      let data = {
        type: this.type,
        exam_id: this.exam ? this.exam.id : null,
        test_id: this.test ? this.test.id : null,
        mistake_id: this.question.mistake_id ?? null,
        question_id: this.question.id,
        answer: this.answer,
        is_missed: missed,
        is_last: this.isLast,
      }
      // decide to show answer immediately
      if (this.showAnswerDirectly) {
        this.showFreeTextCompare = true
        return
      }
      // handle question results
      let result = await this.sendDependingOnType(data)
      this.isLoading = false

      // Handle after result
      if (this.type !== 'exam' || this.exam.direct_solution) {
        this.showAnswerTimer = true
        this.markAnswers(result)
      }

      /**
       * First decide, if direct answer is shown
       * If not continue, if so, decide if manual continue
       * or countdown on TIMEOUT
       */
      if (this.showDirectAnswer) {
        this.hideBtn = true
        let _this = this

        if (this.options && this.options.autoContinue === true) {

          setTimeout(function () {
            _this.handleResponseToExam(result)
          }, _this.TIMEOUT)

        } else {
          this.showAnswerTimer = false
          this.hideContinueBtn = false
          this.tempResult = result
        }

      } else {
        this.handleResponseToExam(result)
      }
    },

    /**
     * Submits the result if temporary result is
     * present
     */
    showNextQuestion: function () {
      if (!this.tempResult) {
        return false
      }
      this.handleResponseToExam(this.tempResult)
    },

    onSelfcheckAnswer: function (results) {
      this.handleResponseToExam(results)
    },

    /**
     *
     * @param data
     * @returns {Promise<*>}
     */
    async sendDependingOnType(data) {
      switch (this.type) {
        case 'mistakes':
          return await sendMistakeAnswer(data)
        default: // exam or practice
          return await sendAnswer(data)
      }
    },

    /**
     * Handles the Response to the exam.
     * Depending if we have results or not, the next questions comes.
     *
     * If we have results show exam results but if we have to selfcheck our text answers
     * show the selfcontrol module
     *
     * @param response
     */
    handleResponseToExam(response) {
      this.isBlocked = true
      // prepare data for parent component
      let data = null
      if (response && response.selfcheck) {
        data = response
      } else if (response && response.results) {
        // TODO: Combine with the above
        data = response
      } else if (response && this.type !== 'exam') {
        // is the result from a single question
        // eslint-disable-next-line vue/no-mutating-props
        this.question.isCorrect = response.is_correct
        // eslint-disable-next-line vue/no-mutating-props
        this.question.wasAnswered = true
        // eslint-disable-next-line vue/no-mutating-props
        this.question.answers = this.answer
        data = response
      }

      // Set the answer to the emitted data
      if (response && response.answer) {
        if (data) {
          data.answer = response.answer
        } else {
          data = response
        }
      }

      this.$emit('answer', (data) ? data : null)
      // show button again
      this.hideBtn = false
    },

    /**
     * Gets the ids back as return and marks the questions
     * corresponding to the feedback
     *
     * @param results
     */
    markAnswers(results) {
      let _this = this
      this.question.wasAnswered = true
      this.question.choices.map((choice, index) => {
        results.right_answered.map(right_choice_id => {
          if (right_choice_id === choice.id) {
            _this.question.choices[index].isCorrect = true
          }
        })

        results.wrong_answered.map(wrong_choice_id => {
          if (wrong_choice_id === choice.id) {
            _this.question.choices[index].isWrong = true
          }
        })

      })

    },
  },
}
</script>

<style scoped>
@media ( max-width: 768px ) {
  .uxfeedback--open {
    position: absolute;
    bottom: 0;
    width: 100%;
    margin: 0 auto;
    text-align: center;
    bottom: -50px;
  }
}
</style>
